import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { ButtonLink } from '@lifeomic/chroma-react/components/ButtonLink';
import { Code, Download } from '@lifeomic/chromicons';
import React from 'react';
import moment from 'moment';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import FileService from '@lifeomic/phc-web-toolkit/src/services/FileService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService/useService';
import { ModaledShareLinkButton } from '@lifeomic/phc-web-toolkit/src/components/ShareLinkButton';
import urls from '../../../../util/urls';
import { CLIHelp } from '@lifeomic/phc-web-toolkit/src/components/CLIHelp';

const fileService = new FileService();

const useStyles = makeStyles(theme => ({
  contentRoot: {
    borderRadius: theme.pxToRem(4),
    border: `1px solid ${theme.palette.divider}`,
  },
  contentHeader: {
    backgroundColor: theme.palette.primary.main,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1),
    color: theme.palette.common.white,
  },
  content: {
    backgroundColor: theme.palette.background.paper,
  },
}));

const messages = defineMessages({
  download: {
    id: 'documentContent.download',
    defaultMessage: 'Download',
  },
  showCode: {
    id: 'documentContent.showCode',
    defaultMessage: 'Toggle Source Code',
  },
});

const formatSize = (size: any) => {
  if (!size) {
    return {
      value: 0,
      unit: 'B',
      raw: 0,
    };
  }
  const exp = size === 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
  return {
    value: (size / Math.pow(1024, exp)).toFixed(2),
    unit: ['B', 'KB', 'MB', 'GB', 'TB'][exp],
    raw: size,
  };
};

export const DocumentContent = ({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  file, // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  children, // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  showCode, // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  setShowCode,
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});

  const [downloadedFile, setDownloadedFile] = React.useState(null);
  const [fetchOpenLink, fetchOpenLinkState] = useService(
    fileService.fetchOpenLink,
  );
  const onClickHandler = () => fetchOpenLink(file.id);

  React.useEffect(() => {
    if (fetchOpenLinkState.response && fetchOpenLinkState.response.data) {
      setDownloadedFile(fetchOpenLinkState.response.data);
    }
  }, [fetchOpenLinkState.response]);

  const createLink = ({
    // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
    activeAccountId,
    // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
    activeProjectId,
    // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
    isCustomClientInUse,
    // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
    type,
  }) => {
    if (!activeAccountId || !activeProjectId) return '';

    if (isCustomClientInUse && type === 'open') {
      const parts = location.host.split('.');
      parts.shift();
      return (
        location.protocol +
        '//' +
        parts.join('.') +
        '/phc' +
        urls.app.files({
          account: activeAccountId,
          projectId: activeProjectId,
          fileId: file.id,
        })
      );
    }

    return (
      location.origin +
      '/phc' +
      urls.app.files({
        account: activeAccountId,
        projectId: activeProjectId,
        fileId: file.id,
      })
    );
  };

  const childrenWithProps = React.Children.map(children, el => {
    if (children && el) {
      return React.cloneElement(el, { showCode: showCode });
    }
  });

  return (
    <Box className={classes.contentRoot} direction="column" fullWidth>
      <Box
        className={classes.contentHeader}
        align="center"
        justify="space-between"
        fullWidth
      >
        <Text size="caption" color="inverse">
          <FormattedMessage
            id="files.fileDetailsModal.documentContent.header.info"
            defaultMessage="{value, number} {unit}  |  {date}"
            values={{
              ...formatSize(file.size),
              date: moment(file.lastModified).format('MM/DD/YY hh:mm A'),
            }}
          />
        </Text>
        <Box>
          <CLIHelp color="inverse">{`lo files download ${file.id}`}</CLIHelp>
          <Tooltip placement="bottom" title={formatMessage(messages.showCode)}>
            <IconButton
              onClick={() =>
                setShowCode((currentShowCode: any) => !currentShowCode)
              }
              color="inverse"
              icon={Code}
              size={0}
              aria-label={formatMessage(messages.showCode)}
            />
          </Tooltip>
          {!downloadedFile && (
            <Tooltip
              placement="bottom"
              title={formatMessage(messages.download)}
            >
              <IconButton
                onClick={onClickHandler}
                color="inverse"
                icon={Download}
                size={0}
                aria-label={formatMessage(messages.download)}
              />
            </Tooltip>
          )}
          {/* @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks. */}
          {downloadedFile && downloadedFile.downloadUrl && (
            <ButtonLink
              color="inverse"
              // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
              to={downloadedFile.downloadUrl}
              variant="text"
            >
              <FormattedMessage
                id="files.fileDetailsModal.documentContent.header.download"
                defaultMessage="Download"
              />
            </ButtonLink>
          )}
          <ModaledShareLinkButton
            color="inverse"
            createLink={createLink}
            placement="bottom-start"
          />
        </Box>
      </Box>
      <Box className={classes.content}>{childrenWithProps}</Box>
    </Box>
  );
};
